import { useMemo } from 'react';

import getDogCareEnabled from '@rover/react-lib/src/pages/provider-profile/ServicePage/utils/getDogCareEnabled';
import { useApiProviderProfileCreateServicesList } from '@rover/rsdk/src/apiClient/latest/api-provider-profile-create-services-list';

export interface UseProviderPetTypesReturn {
  offersCatCare: boolean;
  offersDogCare: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to determine if the provider offers cat care or dog care
 * based on their service pet preferences.
 *
 * @returns {UseProviderPetTypesReturn} Object containing:
 *   - offersCatCare: true if any service accepts cats
 *   - offersDogCare: true if any service accepts dogs of any size
 *   - isLoading: true while fetching services
 *   - error: error message if fetch fails, null otherwise
 *
 */
export const useProviderPetTypes = (): UseProviderPetTypesReturn => {
  const { data: services, isLoading, error } = useApiProviderProfileCreateServicesList();

  const { offersCatCare, offersDogCare } = useMemo(() => {
    if (!services) {
      return { offersCatCare: false, offersDogCare: false };
    }

    const hasCatCare = services.some((service) => service.petPreferences?.cats === true);
    const hasDogCare = services.some((service) =>
      service.petPreferences ? getDogCareEnabled(service.petPreferences) : false
    );

    return {
      offersCatCare: hasCatCare,
      offersDogCare: hasDogCare,
    };
  }, [services]);

  let errorMessage: string | null = null;
  if (error) {
    errorMessage = error instanceof Error ? error.message : 'Failed to fetch provider services';
  }

  return {
    offersCatCare,
    offersDogCare,
    isLoading,
    error: errorMessage,
  };
};

export default useProviderPetTypes;
